import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { getArticle } from '../api/api';
import Article from '../types/Article';
import { ArticleResponse, State, apiStates } from '../types/types';
import { AlmaAccountActions } from '@almamedia-legal-insights/types';

interface UseArticleResult {
  article: Article | null;
  restricted: boolean | null;
  isUpcoming: boolean;
  state: State;
  apiError: AxiosError | null;
  isRefetching: boolean;
}

export function useArticle(
  id?: string,
  actions?: AlmaAccountActions,
  loading = false,
): UseArticleResult {
  const [state, setState] = useState<State>(apiStates.FETCHING);
  const [apiError, setApiError] = useState<AxiosError | null>(null);
  const [hasRefetched, setHasRefetched] = useState(false);
  const [articleData, setArticleData] = useState<{
    article: Article | null;
    restricted: boolean | null;
    isUpcoming: boolean;
  }>({
    article: null,
    restricted: null,
    isUpcoming: false,
  });

  const queryResult = useQuery<ArticleResponse, AxiosError>({
    queryKey: ['article', id],
    queryFn: async () => {
      if (!id) {
        throw new Error('No article ID provided.');
      }
      return getArticle(id, await actions?.getAccessToken());
    },
    enabled: !loading && !!id,
    staleTime: 0,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (
      !hasRefetched &&
      queryResult.data?.restricted &&
      !loading &&
      actions?.getAccessToken
    ) {
      setHasRefetched(true);
      queryResult.refetch();
    }
  }, [
    loading,
    actions?.getAccessToken,
    queryResult.data?.restricted,
    queryResult.refetch,
    hasRefetched,
    queryResult,
  ]);

  useEffect(() => {
    if (queryResult.isFetching) {
      setState(apiStates.FETCHING);
      return;
    }

    if (queryResult.isError) {
      setApiError(queryResult.error);
      setState(apiStates.HAS_ERROR);
      return;
    }

    if (!queryResult.data) {
      return;
    }

    try {
      const { restricted, upcoming, article: articleXML } = queryResult.data;
      const parsedArticle = id ? Article.fromXML(id, articleXML) : null;

      setArticleData({
        restricted,
        article: parsedArticle,
        isUpcoming: upcoming,
      });
      setState(apiStates.READY);
    } catch (error) {
      setState(apiStates.HAS_ERROR);
      setApiError(error as AxiosError);
    }
  }, [
    id,
    queryResult.isFetching,
    queryResult.isError,
    queryResult.data,
    queryResult.error,
  ]);

  return {
    ...articleData,
    state,
    apiError,
    isRefetching: queryResult.isRefetching,
  };
}
